import { useState } from 'react';
import { AlertTriangle, Copy, Check } from 'lucide-react';
import { ApiError, ValidationError } from '../api/client';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

export function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const [copied, setCopied] = useState(false);
  const isApiError = error instanceof ApiError;
  const isValidationError = error instanceof ValidationError;
  const isNetworkError = isApiError && error.status === 0;
  const isServerError = isApiError && error.status >= 500;
  const isClientError = isApiError && error.status >= 400 && error.status < 500;

  let title = 'Something went wrong';
  let message = error.message;
  let showRetry = true;

  const errorDetails = `${error.name}: ${error.message}\n\n${error.stack || 'No stack trace available'}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(errorDetails);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isNetworkError) {
    title = 'Network Error';
    message = 'Unable to connect to the server. Please check your internet connection.';
  } else if (isServerError) {
    title = 'Server Error';
    message = 'The server encountered an error. Please try again in a moment.';
  } else if (isClientError) {
    title = 'Request Error';
    message = error.message;
    showRetry = false;
  } else if (isValidationError) {
    title = 'Data Validation Error';
    message = 'The server returned unexpected data. This may indicate an API version mismatch.';
    showRetry = false;
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="error-fallback"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <AlertTriangle
        className="icon icon-xl"
        aria-hidden="true"
        style={{
          color: 'var(--color-error)',
          marginBottom: '1rem',
        }}
      />
      <h2
        style={{
          fontSize: '1.5rem',
          fontWeight: '600',
          marginBottom: '0.5rem',
          color: 'var(--color-text)',
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontSize: '1rem',
          color: 'var(--color-text-muted)',
          marginBottom: '1.5rem',
          maxWidth: '500px',
        }}
      >
        {message}
      </p>
      {showRetry && (
        <button
          onClick={resetErrorBoundary}
          className="btn btn-primary"
          style={{
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            cursor: 'pointer',
          }}
        >
          Try Again
        </button>
      )}
      <details
        style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: 'var(--color-parchment-dark)',
          borderRadius: '8px',
          maxWidth: '600px',
          width: '100%',
          textAlign: 'left',
        }}
      >
        <summary style={{ cursor: 'pointer', marginBottom: '0.5rem', color: 'var(--color-text-muted)' }}>
          Error Details
        </summary>
        <div style={{ position: 'relative' }}>
          <button
            onClick={handleCopy}
            style={{
              position: 'absolute',
              top: '0.5rem',
              right: '0.5rem',
              padding: '0.5rem',
              background: 'var(--color-paper)',
              border: '1px solid var(--color-border)',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.75rem',
              color: copied ? 'var(--color-success, #4caf50)' : 'var(--color-text-muted)',
            }}
            title="Copy error details"
          >
            {copied ? (
              <>
                <Check size={14} />
                Copied
              </>
            ) : (
              <>
                <Copy size={14} />
                Copy
              </>
            )}
          </button>
          <pre
            style={{
              fontSize: '0.75rem',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              padding: '0.5rem',
              paddingRight: '4rem',
              background: 'var(--color-paper)',
              borderRadius: '4px',
              maxHeight: '300px',
              color: 'var(--color-text)',
            }}
          >
            {errorDetails}
          </pre>
        </div>
      </details>
    </div>
  );
}
